import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FiLock, FiMap } from 'react-icons/fi';

import { trilhaGenerationsApi } from '@/services/trilhaGenerations';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const STEP = { PASSWORD: 1, CONFIG: 2 };

// Estimativas observadas nos seeds locais (gpt-5.5, 2026-07-02).
const MINUTES_PER_BLOCK = 3.5;
const USD_PER_BLOCK = 0.2;

export default function NovaTrilhaModal({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(STEP.PASSWORD);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [etapas, setEtapas] = useState([]);
  const [loadingEtapas, setLoadingEtapas] = useState(false);
  const [etapa, setEtapa] = useState('');
  const [blocksCount, setBlocksCount] = useState('');
  const [name, setName] = useState('');

  const etapaInfo = useMemo(() => etapas.find(e => e.etapa === etapa) || null, [etapas, etapa]);

  // Estável (só usa setters): pode entrar nas deps do effect sem re-disparar fetch.
  const selectEtapa = useCallback(info => {
    setEtapa(info.etapa);
    const min = Math.max(1, info.descriptors_with_content);
    setBlocksCount(String(Math.min(info.total_descriptors, Math.max(min, info.descriptors_with_content + 10))));
    setName(info.learning_path ? '' : `SAEB ${info.label}`);
  }, []);

  // Carrega as etapas ao entrar no passo de configuração.
  useEffect(() => {
    if (!open || step !== STEP.CONFIG || etapas.length > 0) return;
    setLoadingEtapas(true);
    trilhaGenerationsApi
      .etapas()
      .then(json => {
        const list = json?.data || [];
        setEtapas(list);
        if (list.length > 0) selectEtapa(list[0]);
      })
      .catch(() => toast.error('Erro ao carregar etapas da matriz'))
      .finally(() => setLoadingEtapas(false));
  }, [open, step, etapas.length, selectEtapa]);

  const minBlocks = etapaInfo ? Math.max(1, etapaInfo.descriptors_with_content) : 1;
  const maxBlocks = etapaInfo ? etapaInfo.total_descriptors : 1;
  const newBlocks = etapaInfo ? Math.max(0, (Number(blocksCount) || 0) - etapaInfo.descriptors_with_content) : 0;

  const handleClose = () => {
    if (submitting) return;
    setStep(STEP.PASSWORD);
    setPassword('');
    // Zera o cache de etapas: o modal fica montado na página e uma geração concluída
    // muda descriptors_with_content/learning_path — reabrir deve rebuscar do backend.
    setEtapas([]);
    setEtapa('');
    setBlocksCount('');
    setName('');
    onOpenChange(false);
  };

  const handleNext = e => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error('Informe a senha para continuar.');
      return;
    }
    setStep(STEP.CONFIG);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const count = Number(blocksCount);
    if (!etapaInfo) {
      toast.error('Selecione a etapa da matriz.');
      return;
    }
    if (!count || count < minBlocks || count > maxBlocks) {
      toast.error(`Número de blocos deve estar entre ${minBlocks} e ${maxBlocks}.`);
      return;
    }
    if (!etapaInfo.learning_path && !name.trim()) {
      toast.error('Informe o nome da nova trilha.');
      return;
    }
    setSubmitting(true);
    try {
      const json = await trilhaGenerationsApi.create({
        password,
        etapa,
        blocks_count: count,
        learning_path_name: etapaInfo.learning_path ? null : name.trim()
      });
      toast.success('Geração de trilha iniciada — acompanhe o progresso.');
      onSuccess?.(json?.data);
      handleClose();
    } catch (err) {
      const status = err?.response?.status;
      const apiError = err?.response?.data?.errors?.[0];
      if (status === 401) {
        toast.error(apiError?.title || 'Senha incorreta.');
        setStep(STEP.PASSWORD);
        setPassword('');
      } else if (status === 409) {
        toast.error(apiError?.title || 'Já existe uma geração em andamento para esta etapa.');
      } else {
        toast.error(apiError?.title || 'Falha ao iniciar a geração.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary-dark">
            {step === STEP.PASSWORD ? <FiLock /> : <FiMap />}
            {step === STEP.PASSWORD ? 'Autorização' : 'Nova trilha'}
          </DialogTitle>
          <DialogDescription>
            {step === STEP.PASSWORD
              ? 'Informe a senha de autorização da equipe. A geração de conteúdo usa a conta OpenAI configurada no backend.'
              : 'Escolha a etapa da matriz SAEB e o tamanho da trilha. Blocos já gerados são reaproveitados sem custo.'}
          </DialogDescription>
        </DialogHeader>

        {step === STEP.PASSWORD ? (
          <form
            onSubmit={handleNext}
            className="space-y-3"
          >
            <div>
              <Label htmlFor="trilha-gen-password">Senha de autorização</Label>
              <Input
                id="trilha-gen-password"
                type="password"
                autoFocus
                autoComplete="off"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
              />
              <p className="mt-1 text-xs text-neutral">Senha compartilhada da equipe (não é a sua senha pessoal). Não é armazenada.</p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
              >
                Cancelar
              </Button>
              <Button type="submit">Continuar</Button>
            </DialogFooter>
          </form>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            {loadingEtapas ? (
              <p className="py-4 text-center text-sm text-neutral">Carregando etapas...</p>
            ) : (
              <>
                <div>
                  <Label htmlFor="trilha-gen-etapa">Etapa da matriz SAEB</Label>
                  <select
                    id="trilha-gen-etapa"
                    value={etapa}
                    onChange={e => {
                      const info = etapas.find(x => x.etapa === e.target.value);
                      if (info) selectEtapa(info);
                    }}
                    className="w-full rounded border border-neutral-lightest px-3 py-2 text-sm"
                  >
                    {etapas.map(e => (
                      <option
                        key={e.etapa}
                        value={e.etapa}
                      >
                        {e.label} — {e.descriptors_with_content}/{e.total_descriptors} com conteúdo
                      </option>
                    ))}
                  </select>
                  {etapaInfo?.learning_path && (
                    <p className="mt-1 text-xs text-neutral">
                      Esta etapa já tem a trilha <strong>{etapaInfo.learning_path.name}</strong> — a geração vai ampliá-la (nome atual é mantido).
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="trilha-gen-blocks">Número de blocos</Label>
                  <Input
                    id="trilha-gen-blocks"
                    type="number"
                    min={minBlocks}
                    max={maxBlocks}
                    value={blocksCount}
                    onChange={e => setBlocksCount(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-neutral">
                    Entre {minBlocks} e {maxBlocks}. {etapaInfo?.descriptors_with_content ?? 0} blocos já têm conteúdo e são reaproveitados sem custo.
                  </p>
                </div>

                {!etapaInfo?.learning_path && (
                  <div>
                    <Label htmlFor="trilha-gen-name">Nome da trilha</Label>
                    <Input
                      id="trilha-gen-name"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Ex.: SAEB 5º ano EF"
                    />
                  </div>
                )}

                <p className="bg-warning/10 text-warning-dark rounded px-3 py-2 text-xs">
                  {newBlocks > 0
                    ? `${newBlocks} bloco(s) novo(s) serão gerados via IA — estimativa: ≈ ${Math.ceil(newBlocks * MINUTES_PER_BLOCK)} min e ~US$ ${(newBlocks * USD_PER_BLOCK).toFixed(2)}.`
                    : 'Nenhum bloco novo — a trilha será criada/atualizada apenas com o conteúdo existente.'}
                </p>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(STEP.PASSWORD)}
                    disabled={submitting}
                  >
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting ? 'Enviando...' : 'Gerar trilha'}
                  </Button>
                </DialogFooter>
              </>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
